import React, { useEffect, useMemo } from "react";

/**
 * CertificatesSection with duplicate-prevention by certificate number.
 * A cert is considered duplicate if its key (eventNumber || id) is already
 * present in RHF `activities[].certificateNumber`.
 *
 * Props:
 *  - certData, certPage, certLoading, certError
 *  - certChecked, toggleCert
 *  - fetchCertificates(page:number)
 *  - addSelectedToActivities()
 *  - watch (react-hook-form's watch)
 *  - Input, Group (UI helpers)
 */
export default function CertificatesSection(props) {
    const {
        certData,
        certPage,
        certLoading,
        certError,
        certChecked,
        toggleCert,
        fetchCertificates,
        addSelectedToActivities,
        watch,
        Input,
        Group,
    } = props;

    // 1) Take current activities from RHF and build a set of existing certificate numbers
    const activities = watch?.("activities") || [];
    const existingNumbers = useMemo(() => {
        const arr = Array.isArray(activities) ? activities : [];
        return new Set(
            arr
                .map((a) => (a?.certificateNumber != null ? String(a.certificateNumber) : ""))
                .filter(Boolean)
        );
    }, [activities]);

    // 2) Utility: what key we use to identify duplicates for a cert row
    const certKey = (c) => String(c?.id ?? "");
    const isAlreadyAdded = (c) => {
        const key = certKey(c);
        return key ? existingNumbers.has(key) : false;
    };

    // 3) Any checked that are not duplicates -> controls the Add button
    const anyChecked = useMemo(() => {
        const list = Array.isArray(certData?.content) ? certData.content : [];
        return list.some((c) => !!certChecked[c.id] && !isAlreadyAdded(c));
    }, [certChecked, certData?.content, existingNumbers]);

    // 4) Auto-uncheck any ids that became duplicates after activities changed
    useEffect(() => {
        const list = Array.isArray(certData?.content) ? certData.content : [];
        list.forEach((c) => {
            if (isAlreadyAdded(c) && certChecked[c.id]) {
                toggleCert(c.id, false);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingNumbers, certData?.content]);

    return (
        <>
            <style>{`
        .cert-desktop { display: block; }
        .cert-mobile { display: none; }
        .cert-form-grid { display: grid; grid-template-columns: 1fr 160px 120px; gap: 12px; align-items: end; }
        .cert-actions-row { display: flex; gap: 12px; margin-top: 12px; }
        .cert-actions-row .btn { width: 99%; }
        .cert-pagination { display: flex; gap: 8px; align-items: center; margin-top: 8px; }
        .cert-table-wrap { overflow-x: auto; margin-top: 12px; }
        .cert-card { border: 1px solid rgba(0,0,0,.08); border-radius: 10px; padding: 12px; box-shadow: 0 1px 2px rgba(0,0,0,.05); background: #fff; }
        .cert-card + .cert-card { margin-top: 10px; }
        .cert-card input[type="checkbox"] { width: 16px; height: 16px; margin-top: 4px; appearance: auto; }
        .cert-card-row { display: grid; grid-template-columns: 24px 1fr; gap: 10px; align-items: start; }
        .cert-card-title { font-weight: 600; line-height: 1.3; }
        .cert-meta { font-size: 12px; opacity: .8; display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 6px; margin-top: 8px; }
        .cert-meta .item { display: flex; gap: 6px; align-items: center; }
        .cert-meta .label { opacity: .7; }
        .cert-card-actions { margin-top: 8px; display: flex; gap: 8px; align-items: center; }
        .btn-link-sm { font-size: 12px; text-decoration: underline; }
        .muted { opacity: .6; }
        .dup { opacity: .5; }

        @media (max-width: 768px) {
          .cert-form-grid { grid-template-columns: 1fr; }
          .cert-actions-row { flex-direction: column; }
          .cert-pagination { justify-content: space-between; }
          .cert-desktop { display: none; }
          .cert-mobile { display: block; }
        }
      `}</style>

            <Group id="grp-certificates" title={`4. Сертифікати з реєстру (${props?.certData?.totalElements || 0})`} defaultOpen>
                {certError && (
                    <div className="error_text" style={{ marginTop: 8 }}>
                        {certError}
                    </div>
                )}

                {/* Desktop table */}
                <div className="cert-desktop">
                    <div className="cert-table-wrap">
                        <table className="table" style={{ width: "100%", minWidth: 720 }}>
                            <thead>
                            <tr>
                                <th style={{ width: 32 }}></th>
                                <th>Назва / Номер сертифікату</th>
                                <th>№ заходу</th>
                                <th>Дата</th>
                                <th>Години/Бали</th>
                            </tr>
                            </thead>
                            <tbody>
                            {(!certData?.content || certData.content.length === 0) && (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: "center", opacity: 0.7, padding: "12px 0" }}>
                                        Немає даних. <a href="#faq-section">Чому я не бачу сертифікатів?</a>
                                    </td>
                                </tr>
                            )}
                            {certData?.content?.map((c) => {
                                const dup = isAlreadyAdded(c);
                                return (
                                    <tr key={c.id} className={dup ? "dup" : undefined}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={!dup && !!certChecked[c.id]}
                                                disabled={dup}
                                                onChange={(e) => {
                                                    if (dup) return;
                                                    toggleCert(c.id, e.target.checked);
                                                }}
                                            />
                                        </td>
                                        <td>
                                            {c.kind ? ` ${c.kind}: ` : ""}
                                            {c.title || "-"}
                                            {c.id ? ` / №${c.id}` : ""}
                                            {dup && <span className="muted"> (вже додано)</span>}
                                        </td>
                                        <td>{c.eventNumber ?? "-"}</td>
                                        <td>{c.date ? new Date(c.date).toLocaleDateString("uk-UA") : "-"}</td>
                                        <td>
                                            {c.hours && c.points
                                                ? `${c.hours} год. / ${c.points} бал.`
                                                : c.hours
                                                    ? `${c.hours} год.`
                                                    : c.points
                                                        ? `${c.points} бал.`
                                                        : "-"}
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Mobile cards */}
                <div className="cert-mobile" style={{ marginTop: 12 }}>
                    {(!certData?.content || certData.content.length === 0) && (
                        <div className="muted" style={{ textAlign: "center", padding: "12px 0" }}>
                            Немає даних. <a href="#faq-section">Чому я не бачу сертифікатів?</a>
                        </div>
                    )}

                    {certData?.content?.map((c) => {
                        const dup = isAlreadyAdded(c);
                        return (
                            <div className={`cert-card ${dup ? "dup" : ""}`} key={c.id}>
                                <div className="cert-card-row">
                                    <div style={{ alignSelf: "center" }}>
                                        <input
                                            type="checkbox"
                                            checked={!dup && !!certChecked[c.id]}
                                            disabled={dup}
                                            onChange={(e) => {
                                                if (dup) return;
                                                toggleCert(c.id, e.target.checked);
                                            }}
                                            aria-label="Вибрати сертифікат"
                                        />
                                    </div>
                                    <div>
                                        <div className="cert-card-title">
                                            {(c.kind ? `${c.kind}: ` : "") + (c.title || "-")}
                                            {c.id ? ` / №${c.id}` : ""}
                                            {dup && <span className="muted"> (вже додано)</span>}
                                        </div>

                                        <div className="cert-meta">
                                            <div className="item"><span className="label">№ заходу:</span><span>{c.eventNumber ?? "-"}</span></div>
                                            <div className="item"><span className="label">Дата:</span><span>{c.date ? new Date(c.date).toLocaleDateString("uk-UA") : "-"}</span></div>
                                            <div className="item">
                        <span className="label">
                          {c.hours && c.points ? "Год./Бали:" : c.hours ? "Години:" : c.points ? "Бали:" : "Год./Бали:"}
                        </span>
                                                <span>
                          {c.hours && c.points ? `${c.hours} / ${c.points}` : c.hours ? `${c.hours}` : c.points ? `${c.points}` : "-"}
                        </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Pagination */}
                {certData?.totalPages > 1 && (
                    <div className="cert-pagination">
                        <button
                            type="button"
                            className="btn"
                            disabled={certLoading || certData.first}
                            onClick={() => fetchCertificates(Math.max(0, certPage - 1))}
                        >
                            Назад
                        </button>
                        <div style={{ fontSize: 12 }}>
                            Сторінка {certPage + 1} з {certData.totalPages}
                        </div>
                        <button
                            type="button"
                            className="btn"
                            disabled={certLoading || certData.last}
                            onClick={() => fetchCertificates(Math.min(certData.totalPages - 1, certPage + 1))}
                        >
                            Далі
                        </button>
                    </div>
                )}

                {/* Actions */}
                <div className="submit-block cert-actions-row">
                    <button
                        type="button"
                        className="btn"
                        onClick={addSelectedToActivities}
                        disabled={!anyChecked}
                    >
                        Додати у «Види діяльності»
                    </button>
                </div>
            </Group>
        </>
    );
}
